import { SectionProps, useModal } from '@wrisk/ui-components'
import React, { useCallback } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import { Policy, PolicyChangeType, Quote } from '../../../domain'
import { useIsMtaAllowed } from '../../../hooks/adjustments/policy/renewal'
import { useErrorHandlingAsyncCallback } from '../../../hooks/errors'
import { usePrincipal } from '../../authentication'
import { AddOnPath } from '../../product/policy/covers/AddOnPath'
import { PolicyPath } from '../../product/policy/PolicyPath'
import { InRenewalMtaBlockedModal } from '../InRenewalMtaBlockedModal'
import { AddOnSection } from '../product/AddOnSection'

interface AddOnSectionProps extends SectionProps {
  policy: Policy
  quote: Quote
}

export const PolicyAddOnSection: React.FC<AddOnSectionProps> = ({
  policy: { policyId },
  quote,
  ...props
}) => {
  const navigate = useNavigate()
  const modal = useModal()

  const { apiClient } = usePrincipal()
  const isMtaAllowed = useIsMtaAllowed()

  const { loading, execute } = useErrorHandlingAsyncCallback(
    async (
      coverCode: string,
      changeType: PolicyChangeType.ADD_COVER | PolicyChangeType.REMOVE_COVER,
    ) => {
      const { adjustmentId } = await apiClient.createPolicyAdjustment({
        adjustmentType: 'MTA',
        changes: [{ changeType, coverOption: coverCode }],
        policyId,
      })

      navigate(
        generatePath([PolicyPath.ADD_ONS, AddOnPath.REVIEW].join('/'), { adjustmentId }),
      )
    },
  )

  const onChange = useCallback(
    (
      coverCode: string,
      changeType: PolicyChangeType.ADD_COVER | PolicyChangeType.REMOVE_COVER,
    ) =>
      isMtaAllowed
        ? execute(coverCode, changeType)
        : modal.show({ content: <InRenewalMtaBlockedModal /> }),
    [isMtaAllowed, modal, navigate],
  )

  return <AddOnSection loading={loading} quote={quote} onChange={onChange} {...props} />
}
